import React, { useState, useEffect } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Modal, TextField, IconButton, Container, FormControl,
  InputLabel, Select, MenuItem, AppBar, Toolbar, CssBaseline, Chip,
  CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Switch, FormControlLabel, Tooltip, Stack
} from '@mui/material';
import {
  Edit, Delete, PlayArrow, Add, Menu, Search as SearchIcon, Visibility, Save, FileDownload
} from '@mui/icons-material';
import Sidebar from './Sidebar';
import api from '../api/api';

const drawerWidth = 0;
const PRIMARY_DARK_BG = '#1e293b';
const MAIN_BG = '#f1f5f9';

const ReportesMaestros = () => {
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openSidebar, setOpenSidebar] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [categorias, setCategorias] = useState([]);

  // Modal crear/editar
  const [openModal, setOpenModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    categoria: '',
    subcategoria: '',
    sql_query: '',
    parametros: '[]',
    columnas: '[]',
    es_publico: true,
    timeout_segundos: 30,
    orden: 100,
    activo: true,
  });

  // Ejecución de reporte
  const [openResultados, setOpenResultados] = useState(false);
  const [resultadoData, setResultadoData] = useState([]);
  const [resultadoColumnas, setResultadoColumnas] = useState([]);
  const [ejecutando, setEjecutando] = useState(false);
  const [reporteEjecutado, setReporteEjecutado] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getErrorMessage = (err, fallback = 'Ocurrió un error') => {
  const detail = err?.response?.data?.detail;

  if (!detail) return fallback;

  // FastAPI validation errors → array of objects
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        if (typeof e === 'string') return e;
        // e.msg is the human-readable message
        const loc = Array.isArray(e.loc) ? e.loc.filter(l => l !== 'body').join('.') : '';
        return loc ? `${loc}: ${e.msg}` : e.msg || JSON.stringify(e);
      })
      .join(' | ');
  }

  // Simple string
  if (typeof detail === 'string') return detail;

  // Unexpected object
  return typeof detail === 'object' ? JSON.stringify(detail) : String(detail);
};

  // ==================== CARGA INICIAL ====================
  const fetchReportes = async () => {
    try {
      setLoading(true);
      const params = {};
      if (categoriaFiltro) params.categoria = categoriaFiltro;
      if (searchTerm) params.q = searchTerm;

      const res = await api.get('/reportes-maestros/', { params });
      setReportes(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch (err) {
        console.error(err);
        setError(getErrorMessage(err, 'Error al cargar reportes'));
        setReportes([]);
      } finally {
      setLoading(false);
    }
  };

  

  const fetchCategorias = async () => {
    try {
      const res = await api.get('/reportes-maestros/helpers/categorias');
      setCategorias(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.warn('No se pudieron cargar categorías', err);
    }
  };

  useEffect(() => {
    fetchReportes();
    fetchCategorias();
  }, [categoriaFiltro]);

  // Búsqueda con debounce simple
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReportes();
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ==================== MODAL CREAR / EDITAR ====================
  const handleOpen = (reporte = null) => {
    if (reporte) {
      setEditId(reporte.id_reporte);
      setFormData({
        codigo: reporte.codigo || '',
        nombre: reporte.nombre || '',
        descripcion: reporte.descripcion || '',
        categoria: reporte.categoria || '',
        subcategoria: reporte.subcategoria || '',
        sql_query: reporte.sql_query || '',
        parametros: JSON.stringify(reporte.parametros || [], null, 2),
        columnas: JSON.stringify(reporte.columnas || [], null, 2),
        es_publico: reporte.es_publico ?? true,
        timeout_segundos: reporte.timeout_segundos || 30,
        orden: reporte.orden || 100,
        activo: reporte.activo ?? true,
      });
    } else {
      setEditId(null);
      setFormData({
        codigo: '',
        nombre: '',
        descripcion: '',
        categoria: '',
        subcategoria: '',
        sql_query: '',
        parametros: '[]',
        columnas: '[]',
        es_publico: true,
        timeout_segundos: 30,
        orden: 100,
        activo: true,
      });
    }
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    setEditId(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.nombre || !formData.categoria || !formData.sql_query) {
      setError('Nombre, categoría y SQL son obligatorios');
      return;
    }

    let parametrosParsed = [];
    let columnasParsed = [];
    try {
      parametrosParsed = JSON.parse(formData.parametros || '[]');
      columnasParsed = JSON.parse(formData.columnas || '[]');
    } catch (err) {
      console.error(err);
      setError(getErrorMessage(err, 'Error al guardar el reporte'));
      return;   // ← falta esto
    }

    const payload = {
      codigo: formData.codigo || null,
      nombre: formData.nombre,
      descripcion: formData.descripcion || null,
      categoria: formData.categoria,
      subcategoria: formData.subcategoria || null,
      sql_query: formData.sql_query,
      parametros: parametrosParsed,
      columnas: columnasParsed,
      es_publico: formData.es_publico,
      timeout_segundos: Number(formData.timeout_segundos) || 30,
      orden: Number(formData.orden) || 100,
      activo: formData.activo,
    };

    try {
      if (editId) {
        await api.put(`/reportes-maestros/${editId}`, payload);
        notify('Reporte actualizado correctamente');
      } else {
        await api.post('/reportes-maestros/', payload);
        notify('Reporte creado correctamente');
      }
      handleClose();
      fetchReportes();
      fetchCategorias();
    } catch (err) {
      console.error(err);
      setError(getErrorMessage(err, 'Error al guardar el reporte'));
    }
  };

  // ==================== ELIMINAR ====================
  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este reporte?')) return;
    try {
      await api.delete(`/reportes-maestros/${id}`);
      notify('Reporte eliminado');
      fetchReportes();
    } catch (err) {
      notify(getErrorMessage(err, 'Error al eliminar'), 'error');
    }
  };

  // ==================== EJECUTAR REPORTE ====================
  const handleEjecutar = async (reporte) => {
    setEjecutando(true);
    setReporteEjecutado(reporte);
    try {
      const res = await api.post(`/reportes-maestros/${reporte.id_reporte}/ejecutar`, {});
      setResultadoColumnas(res.data.columnas || []);
      setResultadoData(res.data.data || []);
      setOpenResultados(true);
      notify(`Reporte ejecutado: ${res.data.total_registros} registros (${res.data.tiempo_ms} ms)`);
    } catch (err) {
      console.error(err);
      notify(getErrorMessage(err, 'Error al ejecutar el reporte'), 'error');
    } finally {
      setEjecutando(false);
    }
  };

    // ==================== EXPORTAR EXCEL ====================
  const handleExportExcel = async (reporte) => {
    try {
      const response = await api.post(
        `/reportes-maestros/${reporte.id_reporte}/exportar-excel`,
        {}, // aquí van parámetros si los hubiera
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${reporte.codigo || 'reporte'}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      notify('Excel descargado correctamente');
    } catch (err) {
      console.error(err);
      // When responseType is 'blob', the error body is also a blob.
      // Try to parse it if possible:
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          notify(getErrorMessage({ response: { data: json } }, 'Error al exportar Excel'), 'error');
          return;
        } catch (_) { /* ignore parse error */ }
      }
      notify(getErrorMessage(err, 'Error al exportar Excel'), 'error');
    }
  };

  // ==================== RENDER ====================
  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: PRIMARY_DARK_BG,
        }}
      >
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => setOpenSidebar(!openSidebar)} sx={{ mr: 2 }}>
            <Menu />
          </IconButton>
          <Typography variant="h6" noWrap>
            Reportes Dinámicos
          </Typography>
        </Toolbar>
      </AppBar>

      <Sidebar openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          minHeight: '100vh',
          bgcolor: MAIN_BG,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar />

        <Container maxWidth="xl" sx={{ p: 3, flexGrow: 1 }}>
          {/* Barra de acciones */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
              Nuevo Reporte
            </Button>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Buscar por nombre o código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} /> }}
                sx={{ minWidth: 260 }}
              />

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Categoría</InputLabel>
                <Select
                  value={categoriaFiltro}
                  label="Categoría"
                  onChange={(e) => setCategoriaFiltro(e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {categorias.map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Categoría</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Público</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Activo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Ejecuciones</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        No hay reportes registrados
                      </TableCell>
                    </TableRow>
                  ) : (
                    reportes.map((r) => (
                      <TableRow key={r.id_reporte} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {r.codigo || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={600}>{r.nombre}</Typography>
                          {r.descripcion && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {r.descripcion.substring(0, 80)}{r.descripcion.length > 80 ? '…' : ''}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip label={r.categoria} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell>{r.es_publico ? 'Sí' : 'No'}</TableCell>
                        <TableCell>
                          <Chip
                            label={r.activo ? 'Activo' : 'Inactivo'}
                            size="small"
                            color={r.activo ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{r.veces_ejecutado || 0}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ejecutar reporte">
                            <IconButton
                              color="success"
                              onClick={() => handleEjecutar(r)}
                              disabled={ejecutando || !r.activo}
                            >
                              <PlayArrow />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Descargar Excel">
                            <IconButton
                              color="info"
                              onClick={() => handleExportExcel(r)}
                              disabled={!r.activo}
                            >
                              <FileDownload />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton color="primary" onClick={() => handleOpen(r)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton color="error" onClick={() => handleDelete(r.id_reporte)}>
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Container>
      </Box>

      {/* ==================== MODAL CREAR / EDITAR ==================== */}
      <Modal open={openModal} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 720 },
            maxHeight: '92vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {editId ? 'Editar Reporte' : 'Nuevo Reporte'}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Código"
                  name="codigo"
                  value={formData.codigo}
                  onChange={handleChange}
                  placeholder="PAC_EDADES"
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Nombre *"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                />
              </Box>

              <TextField
                fullWidth
                size="small"
                label="Descripción"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                multiline
                rows={2}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Categoría *"
                  name="categoria"
                  value={formData.categoria}
                  onChange={handleChange}
                  required
                  placeholder="Pacientes, Citas, Ventas..."
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Subcategoría"
                  name="subcategoria"
                  value={formData.subcategoria}
                  onChange={handleChange}
                />
              </Box>

              <TextField
                fullWidth
                size="small"
                label="SQL Query *"
                name="sql_query"
                value={formData.sql_query}
                onChange={handleChange}
                required
                multiline
                rows={8}
                sx={{ fontFamily: 'monospace', '& textarea': { fontFamily: 'monospace', fontSize: 13 } }}
                placeholder="SELECT ... FROM ..."
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Parámetros (JSON)"
                  name="parametros"
                  value={formData.parametros}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  sx={{ fontFamily: 'monospace' }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Columnas (JSON)"
                  name="columnas"
                  value={formData.columnas}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  sx={{ fontFamily: 'monospace' }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <FormControlLabel
                  control={<Switch checked={formData.es_publico} onChange={handleChange} name="es_publico" />}
                  label="Público"
                />
                <FormControlLabel
                  control={<Switch checked={formData.activo} onChange={handleChange} name="activo" />}
                  label="Activo"
                />
                <TextField
                  size="small"
                  label="Timeout (seg)"
                  name="timeout_segundos"
                  type="number"
                  value={formData.timeout_segundos}
                  onChange={handleChange}
                  sx={{ width: 120 }}
                />
                <TextField
                  size="small"
                  label="Orden"
                  name="orden"
                  type="number"
                  value={formData.orden}
                  onChange={handleChange}
                  sx={{ width: 100 }}
                />
              </Box>

              {error && <Alert severity="error">{error}</Alert>}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 1 }}>
                <Button variant="outlined" onClick={handleClose}>Cancelar</Button>
                <Button type="submit" variant="contained" startIcon={<Save />}>
                  {editId ? 'Guardar cambios' : 'Crear reporte'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Modal>

      {/* ==================== MODAL RESULTADOS ==================== */}
      <Dialog
        open={openResultados}
        onClose={() => setOpenResultados(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          Resultados: {reporteEjecutado?.nombre}
          <Typography variant="body2" color="text.secondary">
            {resultadoData.length} registros
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {resultadoData.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              El reporte no devolvió registros
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: '70vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {resultadoColumnas.map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultadoData.map((row, idx) => (
                    <TableRow key={idx} hover>
                      {resultadoColumnas.map((col) => (
                        <TableCell key={col}>
                          {row[col] === null || row[col] === undefined
                            ? '—'
                            : String(row[col])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={() => handleExportExcel(reporteEjecutado)}
              disabled={!reporteEjecutado}
            >
              Descargar Excel
            </Button>
            <Button onClick={() => setOpenResultados(false)}>Cerrar</Button>
          </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportesMaestros;